import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import { inspectionApi } from './services/inspectionApi';
import { hasFleetWebAccess } from './services/fleetAccess';
import { useFleetWebNavigation } from './services/fleetWebNavigation';
import {
  inspectionAuth,
  inspectionFirebaseConfigured,
  inspectionFirebaseConfigError,
  waitForInspectionAuthInitialization,
  retryInspectionAuthentication,
} from './services/inspectionFirebase';
import InspectionSignIn from './auth/InspectionSignIn';

const FleetWebWorkspace = lazy(() => import('./components/FleetWebWorkspace'));
const MobileInspectionSurface = lazy(() => import('./MobileInspectionSurface'));
const workspaceFallback = <main className="inspection-project-shell"><p>Loading Fleet workspace…</p></main>;

function readCachedMemberships(uid) {
  const cached = JSON.parse(localStorage.getItem(`fleet-memberships:${uid}`) || 'null');
  if (!cached) return { memberships: [], selectedOrgId: '' };
  if (!Array.isArray(cached.memberships) ||
      cached.memberships.some((organization) => !organization || typeof organization.orgId !== 'string')) {
    throw new Error('Saved organization access has an invalid format.');
  }
  return {
    memberships: cached.memberships,
    selectedOrgId: typeof cached.selectedOrgId === 'string' ? cached.selectedOrgId : '',
  };
}

function getVerifiedSelection(memberships, preferredOrgId, surface) {
  if (surface === 'web' && memberships.length &&
      !memberships.some(hasFleetWebAccess)) {
    return { orgId: '', error: 'Fleet Manager access is required for Fleet web. Use the mobile app for Inspector access.' };
  }
  const eligible = surface === 'web'
    ? memberships.filter(hasFleetWebAccess) : memberships;
  if (!eligible.length) {
    return { orgId: '', error: 'No active Fleet organization membership was found. Contact your administrator.' };
  }
  if (preferredOrgId && !eligible.some((organization) => organization.orgId === preferredOrgId)) {
    return {
      orgId: '',
      error: 'Your selected organization is no longer available to this account. Select another organization if one is available.',
    };
  }
  return { orgId: preferredOrgId || eligible[0].orgId, error: '' };
}

function saveMembershipCache(uid, memberships, selectedOrgId) {
  localStorage.setItem(`fleet-memberships:${uid}`, JSON.stringify({ memberships, selectedOrgId }));
}

function WebInspectionProjectApp(props) {
  const navigation = useFleetWebNavigation();
  return <InspectionProjectSession {...props} navigation={navigation} />;
}

export default function InspectionProjectApp(props) {
  return props.surface === 'web'
    ? <WebInspectionProjectApp {...props} />
    : <InspectionProjectSession {...props} />;
}

function InspectionProjectSession({ surface = 'mobile', navigation }) {
  const [searchParams] = useSearchParams();
  const requestedOrgId = surface === 'web' ? searchParams.get('org') || '' : '';
  const requestedOrgIdRef = useRef(requestedOrgId);
  requestedOrgIdRef.current = requestedOrgId;
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [organizationError, setOrganizationError] = useState('');
  const [accountDeletionStatus, setAccountDeletionStatus] = useState(() =>
    surface === 'web' ? sessionStorage.getItem('fleet-account-deletion') || '' : '');
  // Web content waits until this account's memberships have been verified by the server.
  const [verifiedUid, setVerifiedUid] = useState('');
  const selectedOrgId = useRef('');
  const cacheReadFailed = useRef(false);

  const restoreMemberships = useCallback((currentUser) => {
    setVerifiedUid('');
    cacheReadFailed.current = false;
    if (!currentUser) {
      selectedOrgId.current = '';
      setOrganizations([]);
      setOrgId('');
      setOrganizationError('');
      return;
    }
    try {
      const cached = readCachedMemberships(currentUser.uid);
      selectedOrgId.current = requestedOrgIdRef.current || cached.selectedOrgId;
      setOrganizations(surface === 'web' ? [] : cached.memberships);
      setOrgId(surface !== 'web' && cached.memberships.some((organization) => organization.orgId === cached.selectedOrgId)
        ? cached.selectedOrgId : '');
      setOrganizationError('');
    } catch (cacheError) {
      console.error('Unable to restore cached Fleet organizations:', cacheError);
      cacheReadFailed.current = true;
      selectedOrgId.current = '';
      setOrganizations([]);
      setOrgId('');
      setOrganizationError('Saved organization access could not be read. Reconnect to retry; your inspection drafts and photos have not been removed.');
    }
  }, [surface]);

  useEffect(() => {
    if (!inspectionFirebaseConfigured) {
      setAuthReady(true);
      return undefined;
    }
    let active = true;
    let unsubscribe;
    const fail = (error) => {
      if (!active) return;
      clearTimeout(timer);
      console.error('Inspection authentication startup failed:', error);
      setAuthError(`Unable to start inspection authentication. ${error?.message || 'An unexpected error occurred.'} Check your connection and retry. Saved inspections on this device have not been removed.`);
    };
    const timer = setTimeout(() => fail(new Error('Authentication did not become ready within 15 seconds.')), 15000);
    const start = async () => {
      try {
        await waitForInspectionAuthInitialization();
        if (!active) return;
        unsubscribe = onAuthStateChanged(inspectionAuth, (currentUser) => {
          if (!active) return;
          clearTimeout(timer);
          setAuthError('');
          setUser(currentUser);
          restoreMemberships(currentUser);
          setAuthReady(true);
        }, fail);
      } catch (error) {
        fail(error);
      }
    };
    start();
    return () => {
      active = false;
      clearTimeout(timer);
      unsubscribe?.();
    };
  }, [restoreMemberships]);

  const handleSignIn = (authenticatedUser) => {
    if (surface === 'web') {
      sessionStorage.removeItem('fleet-account-deletion');
      setAccountDeletionStatus('');
    }
    restoreMemberships(authenticatedUser);
    setUser(authenticatedUser);
  };

  useEffect(() => {
    let active = true;
    let requestPending = false;
    if (!user) return undefined;
    const loadOrganizations = () => {
      if (!navigator.onLine) {
        if (surface === 'web') {
          setOrganizations([]);
          setOrgId('');
          setOrganizationError('Offline: reconnect to verify organization access.');
        } else {
          setOrganizationError('Offline: using this account’s saved organizations. Access will be checked again when reconnected.');
        }
        return;
      }
      if (requestPending) return;
      requestPending = true;
      inspectionApi.getOrganizations()
        .then((memberships) => {
          if (!active) return;
          const eligible = Array.isArray(memberships) ? memberships : [];
          setOrganizations(eligible);
          setVerifiedUid(user.uid);
          const selection = getVerifiedSelection(eligible, requestedOrgIdRef.current || selectedOrgId.current, surface);
          selectedOrgId.current = selection.orgId || selectedOrgId.current;
          let cacheWriteFailed = false;
          try {
            saveMembershipCache(user.uid, eligible, selectedOrgId.current);
          } catch (cacheError) {
            console.error('Unable to cache Fleet organizations:', cacheError);
            cacheWriteFailed = true;
          }
          setOrgId(selection.orgId);
          if (selection.error) setOrganizationError(selection.error);
          else if (cacheWriteFailed) setOrganizationError('Organization access is available, but offline organization restoration could not be saved. Existing drafts have not been removed.');
          else if (cacheReadFailed.current) setOrganizationError('Saved organization access was unreadable and has been restored from verified membership. Inspection drafts have not been removed.');
          else setOrganizationError('');
        })
        .catch((error) => {
          console.error('Failed to load inspection organization memberships:', error);
          if (!active) return;
          if (surface === 'web') {
            setOrganizations([]);
            setOrgId('');
            setOrganizationError(error.message || 'Unable to verify organization access. Reconnect to retry.');
            return;
          }
          if (!navigator.onLine) {
            setOrganizationError('Offline: using this account’s saved organizations. Access will be checked again when reconnected.');
            return;
          }
          setOrganizationError(error.message || 'Unable to load inspection organizations.');
        })
        .finally(() => { requestPending = false; });
    };
    loadOrganizations();
    window.addEventListener('online', loadOrganizations);
    if (surface === 'web') window.addEventListener('offline', loadOrganizations);
    if (surface === 'web') window.addEventListener('focus', loadOrganizations);
    const refreshTimer = surface === 'web' ? setInterval(loadOrganizations, 15000) : null;
    return () => {
      active = false;
      window.removeEventListener('online', loadOrganizations);
      window.removeEventListener('offline', loadOrganizations);
      window.removeEventListener('focus', loadOrganizations);
      if (refreshTimer) clearInterval(refreshTimer);
    };
  }, [user, surface]);

  const selectOrganization = useCallback((nextOrgId, updateUrl = true) => {
    const selection = getVerifiedSelection(organizations, nextOrgId, surface);
    selectedOrgId.current = nextOrgId;
    setOrgId(selection.orgId);
    setOrganizationError(selection.error);
    if (selection.error) return;
    if (surface === 'web' && updateUrl) {
      // Changing organization clears open record IDs and organization-specific filters.
      navigation.go(navigation.valid ? navigation.section : 'dashboard', '', { organization: nextOrgId });
      return;
    }
    try {
      saveMembershipCache(user.uid, organizations, nextOrgId);
    } catch (error) {
      console.error('Unable to save the selected Fleet organization:', error);
      setOrganizationError('Your selected organization could not be saved for reopening. Existing inspections remain in their original organization.');
    }
  }, [navigation, organizations, surface, user]);

  // Back/Forward and direct links select the URL's organization; the workspace adds a missing org parameter.
  useEffect(() => {
    if (surface !== 'web' || !user) return;
    if (requestedOrgId && requestedOrgId !== selectedOrgId.current && organizations.length) {
      selectOrganization(requestedOrgId, false);
    }
  }, [organizations, requestedOrgId, selectOrganization, surface, user]);

  const handleSignOut = async () => {
    try {
      await signOut(inspectionAuth);
      setUser(null);
      setOrganizations([]);
      setOrgId('');
    } catch (error) {
      console.error('Inspection-project sign-out failed:', error);
      setOrganizationError(error.message || 'Unable to sign out.');
    }
  };

  const refreshOrganizations = async () => {
    const memberships = await inspectionApi.getOrganizations();
    const eligible = Array.isArray(memberships) ? memberships : [];
    setOrganizations(eligible);
    setVerifiedUid(user.uid);
    const selection = getVerifiedSelection(eligible, requestedOrgIdRef.current || selectedOrgId.current, surface);
    selectedOrgId.current = selection.orgId || selectedOrgId.current;
    setOrgId(selection.orgId);
    setOrganizationError(selection.error);
    try {
      saveMembershipCache(user.uid, eligible, selectedOrgId.current);
    } catch (error) {
      console.error('Unable to cache refreshed Fleet organizations:', error);
      setOrganizationError('Organization access is available, but offline organization restoration could not be saved. Existing drafts have not been removed.');
    }
    return eligible;
  };

  if (!inspectionFirebaseConfigured) {
    return (
      <main className="inspection-project-shell">
        <section className="inspection-project-card">
          <h1>Inspection project is not configured</h1>
          <p role="alert">{inspectionFirebaseConfigError}</p>
          <a href="/">Return to driver app</a>
        </section>
      </main>
    );
  }
  if (authError) {
    return (
      <main className="inspection-project-shell">
        <section className="inspection-project-card">
          <h1>Inspection authentication could not start</h1>
          <p className="inspection-error" role="alert">{authError}</p>
          <button className="inspection-primary-button" type="button" onClick={retryInspectionAuthentication}>Retry authentication</button>
        </section>
      </main>
    );
  }
  if (!authReady) return <main className="inspection-project-shell"><p>Loading inspection authentication…</p></main>;
  if (!user) return <InspectionSignIn onSignIn={handleSignIn}
    accountDeletionStatus={surface === 'web' ? accountDeletionStatus : ''} />;

  if (surface === 'web') {
    const verifiedOrgId = verifiedUid !== user.uid || (requestedOrgId && requestedOrgId !== orgId) ? '' : orgId;
    const canAdmin = organizations.find((organization) => organization.orgId === verifiedOrgId)?.fleetAdmin === true;
    return (
      <Suspense fallback={workspaceFallback}>
        <FleetWebWorkspace
          key={`${user.uid}:${verifiedOrgId}:${canAdmin}`}
          user={user}
          organizations={organizations.filter(hasFleetWebAccess)}
          orgId={verifiedOrgId}
          timeZone={organizations.find((organization) => organization.orgId === verifiedOrgId)?.timezone || 'America/Chicago'}
          canAdmin={canAdmin}
          onOrgChange={selectOrganization}
          onOrganizationsRefresh={refreshOrganizations}
          organizationError={organizationError || (verifiedUid === user.uid ? '' : 'Verifying organization access...')}
          onSignOut={handleSignOut}
          onAccountDeleted={(status) => {
            setAccountDeletionStatus(status);
            setUser(null);
            setOrganizations([]);
            setOrgId('');
          }}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={workspaceFallback}>
      <MobileInspectionSurface
        user={user}
        organizations={organizations}
        orgId={orgId}
        timeZone={organizations.find((organization) => organization.orgId === orgId)?.timezone || 'America/Chicago'}
        onOrgChange={selectOrganization}
        organizationError={organizationError}
        onSignOut={handleSignOut}
      />
    </Suspense>
  );
}
